import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { MentorCommentsDisplay, MentorInlineComment } from './MentorComponents.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, detectUnconstructiveContent, detectConnotationIssues, notifyMentorOfHarmDetection, sendNotification, sendPushToUser, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, useRelationships } from '../shared.jsx';

function ExploreAlternativesStep({ conflict, user, canEdit, onUpdate, onReturnToOverview }) {
  const toast = useToast();
  const showCrisisModal = useCrisisAlert();
  const [alternative, setAlternative] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [lateReason, setLateReason] = useState('');
  const [alternativeConnotation, setAlternativeConnotation] = useState(null);

  const isMentee = conflict.mentees?.some(m => m.id === user.id || m.email?.toLowerCase() === user.email?.toLowerCase()) ||
                   conflict.createdBy === user.id;
  const isMentor = user.role === 'mentor';
  const { relationships: allRelationships } = useRelationships();

  // Derive mentor IDs from relationships (for notifications)
  const mentorIdsForConflict = React.useMemo(() => {
    const participantIds = (conflict.mentees || []).map(m => m.id);
    const activeRels = (allRelationships || []).filter(r => r.status === 'active' && r.type === 'mentor-participant');
    const ids = new Set();
    activeRels.forEach(r => {
      const mid = r.mentorId || (r.initiatorRole === 'mentor' ? r.initiatorId : r.receiverId);
      const pid = r.participantId || (r.initiatorRole === 'participant' ? r.initiatorId : r.receiverId);
      if (participantIds.includes(pid)) ids.add(mid);
    });
    return [...ids];
  }, [conflict.id, allRelationships]);
  const alternatives = conflict.steps.exploreAlternatives?.alternatives || [];
  const userAlternatives = alternatives.filter(a => a.createdBy === user.id);
  const maxPerUser = 3;

  // GUARD: Users can only add alternatives when the conflict has actually reached explore-alternatives status.
  // This prevents users from clicking the step tab and adding alternatives while communication is still active.
  // The status only becomes 'explore-alternatives' when either:
  //   (a) All 5 communication cycles have completed (auto-transition), OR
  //   (b) ALL assigned mentees voted "satisfied" in the communication satisfaction vote
  const communicationDone = conflict.status === 'explore-alternatives' || 
                            conflict.status === 'evaluate-select' || 
                            conflict.status === 'agree-implement' || 
                            conflict.status === 'follow-up' || 
                            conflict.status === 'resolved';

  // Get the time when this step started (when status changed to explore-alternatives)
  const stepStartTime = conflict.statusHistory?.find(h => h.status === 'explore-alternatives')?.timestamp;
  
  // Calculate time remaining
  const calculateTimeRemaining = () => {
    if (!stepStartTime) return maxPerUser * conflict.responseWindow;
    const startTime = new Date(stepStartTime);
    const now = new Date();
    const minutesPassed = (now - startTime) / (1000 * 60);
    const totalAllowedTime = maxPerUser * conflict.responseWindow; // 3 alternatives * responseWindow minutes each
    const remaining = Math.max(0, totalAllowedTime - minutesPassed);
    return remaining;
  };

  const formatTimeRemaining = (minutes) => {
    const totalSeconds = Math.ceil(minutes * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isTimeExpired = () => {
    return calculateTimeRemaining() <= 0;
  };

  // Update timer every second
  React.useEffect(() => {
    setTimeRemaining(calculateTimeRemaining());
    
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [stepStartTime, alternatives]);

  const containsProfanity = (text) => {
    const profanityList = [
      'damn', 'damned', 'damning',
      'hell',
      'crap', 'crappy', 'crapping',
      'shit', 'shitty', 'shitting', 'shits', 'bullshit',
      'fuck', 'fucking', 'fucked', 'fucker', 'fuckers', 'fucks', 'fuckface', 'fuckhead', 'fuckwit',
      'bitch', 'bitchy', 'bitching', 'bitches',
      'ass', 'asses',
      'asshole', 'assholes',
      'dumbass', 'dumbasses',
      'jackass', 'jackasses',
      'smartass', 'badass',
      'bastard', 'bastards',
      'piss', 'pissed', 'pissing', 'pissy',
      'dick', 'dicks', 'dickhead', 'dickheads',
      'cock', 'cocks', 'cocksucker',
      'pussy', 'pussies',
      'whore', 'whores', 'whoring',
      'slut', 'slutty', 'sluts',
      'fag', 'fags', 'faggot', 'faggots',
      'nigger', 'niggers', 'nigga', 'niggas',
      'retard', 'retarded', 'retards',
      'cunt', 'cunts',
      'twat', 'twats',
      'wank', 'wanker', 'wankers', 'wanking',
      'bollocks',
      'prick', 'pricks',
      'arse', 'arsehole', 'arseholes',
      'bugger', 'buggers',
      'bloody',
      'motherfucker', 'motherfucking', 'motherfuckers',
      // UK / regional slang
      'shite', 'gobshite', 'bellend', 'bellends', 'minger', 'minging',
      'tosser', 'tossers', 'git', 'pillock', 'numpty', 'wazzock',
      'knobhead', 'knob', 'nonce', 'slag', 'slags', 'chav', 'plonker',
      // Abbreviations of profanity
      'af', 'stfu', 'wtf', 'gtfo', 'lmfao', 'fml', 'smfh', 'pos',
      'bs', 'omfg', 'ffs', 'jfc', 'mf', 'mfer', 'sob', 'effing',
      'fking', 'fkn', 'azz', 'biatch', 'beyotch', 'shyt', 'sht'
    ];
    const lowerText = text.toLowerCase();
    // Normalize leetspeak / symbol substitutions
    const normalizedText = lowerText
      .replace(/\$/g, 's')
      .replace(/@/g, 'a')
      .replace(/!/g, 'i')
      .replace(/1(?=[a-z])/gi, 'i')
      .replace(/3(?=[a-z])/gi, 'e')
      .replace(/0(?=[a-z])/gi, 'o')
      .replace(/5(?=[a-z])/gi, 's')
      .replace(/\*/g, 'u')
      .replace(/\+(?=[a-z])/gi, 't');
    return profanityList.some(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'i');
      return regex.test(lowerText) || regex.test(normalizedText);
    });
  };

  const handleSubmitAlternative = async () => {
    if (!alternative.trim()) {
      toast('Please enter a proposed solution before submitting.');
      return;
    }
    if (!communicationDone) {
      toast('Cannot add alternatives until communication is complete. All participants must agree or all communication cycles must finish.');
      return;
    }
    if (userAlternatives.length >= maxPerUser) {
      toast('You have already submitted 3 alternatives');
      return;
    }

    const isLate = isTimeExpired() && userAlternatives.length === 0;
    
    if (isLate && lateReason.trim().length < 10) {
      toast('Please provide a reason for submitting late (minimum 10 characters)');
      return;
    }
    if (isLate && containsProfanity(lateReason)) {
      toast('Your late submission reason contains inappropriate language. Please revise it.');
      return;
    }

    // Block profanity in the alternative itself
    if (containsProfanity(alternative)) {
      toast('Your proposed solution contains inappropriate language. Please revise it before submitting.');
      return;
    }

    // Block unconstructive/nasty content
    const toneCheck = detectUnconstructiveContent(alternative);
    if (toneCheck.isUnconstructive) {
      toast(toneCheck.reason || 'Please keep your proposed solution respectful and constructive.');
      return;
    }

    // Check for inflammatory connotations — warn but allow override
    const connotation = detectConnotationIssues(alternative);
    if (connotation.hasIssues && !alternativeConnotation?._dismissed) {
      setAlternativeConnotation(connotation);
      return;
    }

    const newAlternative = {
      id: `alt-${Date.now()}`,
      text: alternative,
      createdBy: user.id,
      createdByName: user.name,
      createdByEmail: user.email,
      timestamp: new Date().toISOString(),
      ...(isLate && {
        lateSubmission: true,
        lateReason: lateReason.trim(),
        minutesLate: Math.ceil(Math.abs(timeRemaining))
      })
    };

    const newAlternatives = [...alternatives, newAlternative];
    const newUserAlternatives = newAlternatives.filter(a => a.createdBy === user.id);
    
    // Auto-mark as done if this is the 3rd alternative
    const doneUsers = conflict.steps?.exploreAlternatives?.doneUsers || [];
    let updatedDoneUsers = [...doneUsers];
    if (newUserAlternatives.length >= maxPerUser && !updatedDoneUsers.includes(user.id)) {
      updatedDoneUsers.push(user.id);
    }

    // Check if all mentees are done (either marked done or submitted max)
    const menteeIds = conflict.mentees?.map(m => m.id) || [];
    const allDone = menteeIds.every(id => updatedDoneUsers.includes(id));

    const updated = {
      ...conflict,
      steps: {
        ...conflict.steps,
        exploreAlternatives: {
          ...conflict.steps.exploreAlternatives,
          alternatives: newAlternatives,
          doneUsers: updatedDoneUsers
        }
      },
      ...(allDone && {
        status: 'evaluate-select',
        statusHistory: [
          ...conflict.statusHistory,
          { status: 'evaluate-select', timestamp: new Date().toISOString() }
        ]
      }),
      updatedAt: new Date().toISOString(),
      updatedBy: user.id
    };

    // Feature 2: Resolution Mentoring Gate — if mentor has it enabled,
    // mark alternatives as pending mentor review instead of jumping to evaluate-select
    if (allDone && mentorIdsForConflict.length > 0) {
      try {
        const mentorSettingsId = mentorIdsForConflict[0];
        const mentorSettings = await storage.get(`mentor-settings:${mentorSettingsId}`, true);
        if (mentorSettings) {
          const override = mentorSettings.conflictOverrides?.[conflict.id]?.resolutionMentoringEnabled;
          const effective = override !== undefined ? override : mentorSettings.resolutionMentoringEnabled;
          if (effective) {
            // Keep status at explore-alternatives but flag as waiting-for-mentor
            updated.status = 'explore-alternatives';
            updated.statusHistory = conflict.statusHistory; // Don't add evaluate-select yet
            updated.steps.exploreAlternatives.mentorReviewRequired = true;
            updated.steps.exploreAlternatives.mentorReviewComplete = false;
            // Notify mentor(s)
            mentorIdsForConflict.forEach(mid => {
              sendPushToUser(mid, 'Alternatives Ready for Review', `${conflict.title}: All participants have proposed solutions. Your review is needed before voting.`, { conflictId: conflict.id });
            });
          }
        }
      } catch (e) { /* proceed normally if settings can't be loaded */ }
    }

    addAuditEntry(updated, user.id, user.name, 'Submitted alternative', `Alternative ${newUserAlternatives.length} of ${maxPerUser}`, user.email);
    await storage.set(`conflict:${conflict.id}`, updated, true);
    // Harm detection — check after saving (never block the user)
    const harmResult = detectHarmContent(alternative);
    if (harmResult.detected) {
      showCrisisModal(harmResult.type);
      notifyMentorOfHarmDetection(conflict, user, harmResult.type, 'Explore Alternatives submission');
    }
    setAlternative('');
    setLateReason('');
    setAlternativeConnotation(null);
    await onUpdate();

    if (allDone) {
      toast('All participants are done. Moving to Evaluate & Select!');
      if (onReturnToOverview) onReturnToOverview();
    } else if (newUserAlternatives.length >= maxPerUser) {
      // User hit max alternatives, auto-marked done, nothing more to do
      if (onReturnToOverview) onReturnToOverview();
    }
  };

  return (
    <div className="space-y-6">
      {/* Resolution from Identify & Define */}
      {conflict.steps?.identifyDefine?.resolutionCheck?.canResolve && conflict.steps?.identifyDefine?.resolutionCheck?.resolution && (
        <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-lg text-green-800 mb-1">
                ✓ Early Resolution Proposed
              </p>
              <div className="text-sm text-green-700">
                Proposed by {conflict.steps.identifyDefine.resolutionCheck.proposedByEmail || 
                            conflict.steps.identifyDefine.resolutionCheck.proposedByName || 
                            'Participant'} · {new Date(conflict.steps.identifyDefine.resolutionCheck.timestamp).toLocaleString()}
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-white rounded-lg border border-green-300 mb-4">
            <div className="text-sm font-medium text-green-800 mb-1">Proposed Resolution:</div>
            <p className="text-stone-700">{conflict.steps.identifyDefine.resolutionCheck.resolution}</p>
          </div>

          {/* Accept/Reject for other mentees */}
          {canEdit && conflict.steps.identifyDefine.resolutionCheck.proposedBy !== user.id &&
           !conflict.steps.identifyDefine.resolutionCheck.responses?.[user.id] && (
            <div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-amber-800 font-medium">Your Response Required</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={async () => {
                    const updated = {
                      ...conflict,
                      steps: {
                        ...conflict.steps,
                        identifyDefine: {
                          ...conflict.steps.identifyDefine,
                          resolutionCheck: {
                            ...conflict.steps.identifyDefine.resolutionCheck,
                            responses: {
                              ...(conflict.steps.identifyDefine.resolutionCheck.responses || {}),
                              [user.id]: {
                                accepted: true,
                                userId: user.id,
                                userEmail: user.email,
                                userName: user.name,
                                timestamp: new Date().toISOString()
                              }
                            }
                          }
                        }
                      },
                      updatedAt: new Date().toISOString(),
                      updatedBy: user.id
                    };

                    const allResponses = Object.values(updated.steps.identifyDefine.resolutionCheck.responses || {});
                    const allAccepted = allResponses.every(r => r.accepted);
                    const allMenteesResponded = allResponses.length >= (conflict.mentees?.length || 1);

                    if (allAccepted && allMenteesResponded) {
                      updated.status = 'evaluate-select';
                      updated.statusHistory.push({
                        status: 'evaluate-select',
                        timestamp: new Date().toISOString()
                      });
                    }

                    addAuditEntry(updated, user.id, user.name, 'Accepted proposed resolution', null, user.email);
                    await storage.set(`conflict:${conflict.id}`, updated, true);
                    await onUpdate();
                    toast('You have accepted the proposed resolution!');
                    if (onReturnToOverview) onReturnToOverview();
                  }}
                  className="py-3 bg-green-700 hover:bg-green-800 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Accept
                </button>
                <button
                  onClick={async () => {
                    const updated = {
                      ...conflict,
                      steps: {
                        ...conflict.steps,
                        identifyDefine: {
                          ...conflict.steps.identifyDefine,
                          resolutionCheck: {
                            ...conflict.steps.identifyDefine.resolutionCheck,
                            responses: {
                              ...(conflict.steps.identifyDefine.resolutionCheck.responses || {}),
                              [user.id]: {
                                accepted: false,
                                userId: user.id,
                                userEmail: user.email,
                                userName: user.name,
                                timestamp: new Date().toISOString()
                              }
                            }
                          }
                        }
                      },
                      status: 'communicate',
                      statusHistory: [
                        ...conflict.statusHistory,
                        {
                          status: 'communicate',
                          timestamp: new Date().toISOString()
                        }
                      ],
                      updatedAt: new Date().toISOString(),
                      updatedBy: user.id
                    };

                    addAuditEntry(updated, user.id, user.name, 'Rejected proposed resolution', 'Conflict returned to Communication phase', user.email);
                    await storage.set(`conflict:${conflict.id}`, updated, true);
                    await onUpdate();
                    toast('Resolution rejected. Moving to Communication step.');
                    if (onReturnToOverview) onReturnToOverview();
                  }}
                  className="py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                  <AlertCircle className="w-5 h-5" />
                  Reject
                </button>
              </div>
            </div>
          )}

          {/* Responses Status */}
          {conflict.steps.identifyDefine.resolutionCheck.responses && 
           Object.keys(conflict.steps.identifyDefine.resolutionCheck.responses).length > 0 && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-stone-200">
              <div className="text-sm font-medium text-stone-700 mb-2">Responses:</div>
              <div className="space-y-2">
                {Object.values(conflict.steps.identifyDefine.resolutionCheck.responses).map((response, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    {response.accepted ? (
                      <CheckCircle className="w-4 h-4 text-green-700" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className={response.accepted ? 'text-green-700' : 'text-red-700'}>
                      {response.userEmail || response.userName} {response.accepted ? 'accepted' : 'rejected'}
                    </span>
                    <span className="text-stone-500 text-xs">
                      · {new Date(response.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <h4 className="font-medium text-stone-800 mb-3">Explore Alternatives</h4>
        <p className="text-sm text-stone-600 mb-4">
          Each participant can propose up to 3 potential solutions. Be creative and constructive. 
          You can only see your own alternatives until the evaluation phase.
        </p>
      </div>

      {/* Show challenges from Identify & Define as context for proposing solutions */}
      {(conflict.steps?.identifyDefine?.challenges || []).length > 0 && (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-amber-700" />
            <span className="text-sm font-medium text-amber-800">Challenges to Consider</span>
          </div>
          <p className="text-xs text-amber-700 mb-3">These constraints were identified during Identify & Define. Keep them in mind when proposing solutions.</p>
          <div className="space-y-1.5">
            {(conflict.steps.identifyDefine.challenges).map(ch => (
              <div key={ch.id} className="flex items-start gap-2 text-sm">
                <span className="text-amber-700 mt-0.5 flex-shrink-0">•</span>
                <span className="text-stone-700">{ch.text}</span>
                <span className="text-[10px] text-stone-500 flex-shrink-0 mt-0.5">({ch.userName || ch.userEmail})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show only user's own alternatives during explore phase */}
      {/* After evaluate-select is complete, show all */}
      {(() => {
        const isExplorePhase = conflict.status === 'explore-alternatives';
        const evaluateComplete = conflict.steps?.evaluateSelect?.completed;
        const visibleAlternatives = isExplorePhase && !evaluateComplete
          ? alternatives.filter(a => a.createdBy === user.id)
          : alternatives;

        return visibleAlternatives.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleAlternatives.map((alt, idx) => (
              <div key={alt.id} className="bg-white rounded-lg p-4 border border-stone-200 overflow-hidden">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-medium flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-stone-700 mb-2 break-words">{alt.text}</p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-stone-500 mb-2 gap-1">
                      <span className="break-words">
                        {alt.createdBy === user.id ? 'By you' : `By ${alt.createdByEmail || alt.createdByName}`}
                      </span>
                      <span className="flex-shrink-0">{new Date(alt.timestamp).toLocaleString()}</span>
                    </div>
                    {alt.lateSubmission && (
                      <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                        <div className="flex items-center gap-1 text-xs text-red-700 font-medium mb-1">
                          <AlertCircle className="w-3 h-3" />
                          Late Submission ({alt.minutesLate} min late)
                        </div>
                        <p className="text-xs text-red-700 italic break-words">"{alt.lateReason}"</p>
                      </div>
                    )}
                    {/* Mentor comments on alternative */}
                    <MentorInlineComment entryId={`alt-${alt.id}`} entryType="alternative" conflict={conflict} user={user} onUpdate={onUpdate} />
                    <MentorCommentsDisplay entryId={`alt-${alt.id}`} conflict={conflict} user={user} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null;
      })()}

      {/* BLOCKED: Communication not yet complete */}
      {!communicationDone && (
        <div className="p-5 bg-amber-50 rounded-lg border-2 border-amber-300">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-amber-700" />
            <h5 className="font-medium text-amber-800">Communication Phase Still Active</h5>
          </div>
          <p className="text-sm text-amber-700">
            Alternatives cannot be added until the communication phase is complete. 
            This requires either all {5} communication cycles to finish, or all assigned mentees to agree 
            that communication is done.
          </p>
        </div>
      )}

      {/* Add Alternative Form — only when communication is done */}
      {(communicationDone && (isMentee || isMentor) && canEdit && userAlternatives.length < maxPerUser && 
        !conflict.steps?.exploreAlternatives?.doneUsers?.includes(user.id)) && (
        <div className="bg-white rounded-lg p-4 border-2 border-dashed border-purple-300">
          {/* Timer Display */}
          {stepStartTime && (
            <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-purple-800">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">Time to Submit Alternatives</span>
                </div>
                <div className="text-lg font-mono font-bold text-purple-900">
                  {formatTimeRemaining(timeRemaining)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="h-2 bg-purple-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-600 transition-all duration-1000 ease-linear"
                      style={{ 
                        width: `${Math.max(0, (timeRemaining / (maxPerUser * conflict.responseWindow)) * 100)}%` 
                      }}
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs text-purple-700 mt-2">
                {isTimeExpired() ? (
                  <span className="text-red-700 font-medium">⚠️ Time has expired. You must provide a reason for late submission.</span>
                ) : (
                  <>Total time: {maxPerUser * conflict.responseWindow} minutes ({maxPerUser} alternatives × {conflict.responseWindow} min each). At least 1 required.</>
                )}
              </p>
            </div>
          )}

          <div className="relative">
            <textarea
              value={alternative}
              onChange={(e) => {
                setAlternative(e.target.value);
                // Reset connotation warning when user edits text
                if (alternativeConnotation) setAlternativeConnotation(null);
              }}
              className="w-full px-4 py-3 pr-12 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mb-2"
              placeholder="Describe a potential solution..."
              rows={3}
              maxLength={500}
            />
            <VoiceInputButton 
              onResult={(text) => setAlternative(prev => prev + (prev ? ' ' : '') + text)}
              className="absolute right-2 top-2"
            />
          </div>

          {/* Profanity indicator */}
          {alternative.trim().length > 0 && (
            <div className={`text-sm flex items-center gap-2 font-medium mb-2 ${
              !containsProfanity(alternative) 
                ? 'text-green-700' 
                : 'text-red-600'
            }`}>
              {!containsProfanity(alternative) ? (
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
              ) : (
                <X className="w-4 h-4 flex-shrink-0" />
              )}
              No profanity or offensive language
            </div>
          )}

          {/* Unconstructive / dismissive language indicator */}
          {alternative.trim().length >= 5 && (() => {
            const toneCheck = detectUnconstructiveContent(alternative);
            return toneCheck.isUnconstructive ? (
              <div className="text-sm flex items-start gap-2 font-medium mb-2 text-red-600">
                <X className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{toneCheck.reason || 'Please keep your proposed solution respectful and constructive.'}</span>
              </div>
            ) : (
              <div className="text-sm flex items-center gap-2 font-medium mb-2 text-green-700">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                Constructive and respectful tone
              </div>
            );
          })()}

          {/* Connotation / Word Choice Suggestions for alternative */}
          {alternativeConnotation && alternativeConnotation.hasIssues && !alternativeConnotation._dismissed && (
            <div className="mb-3 p-3 rounded-lg border bg-blue-50 border-blue-200">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 mb-2">Word choice suggestion</p>
                  {alternativeConnotation.suggestions.map((s, i) => (
                    <div key={i} className="mb-2 last:mb-0">
                      <p className="text-xs text-blue-700 mb-1">{s.context}</p>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded line-through">"{s.found}"</span>
                        <span className="text-blue-600">→</span>
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">"{s.suggestion}"</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setAlternativeConnotation({ ...alternativeConnotation, _dismissed: true })} className="text-xs text-blue-600 hover:text-blue-800 font-medium underline">Keep my wording and submit</button>
                    <span className="text-xs text-blue-600">|</span>
                    <button onClick={() => setAlternativeConnotation(null)} className="text-xs text-green-700 hover:text-green-800 font-medium underline">I'll revise my text</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Late Reason Field - Only show if time expired and no alternatives submitted yet */}
          {isTimeExpired() && userAlternatives.length === 0 && (
            <div className="mb-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <label className="block text-sm font-medium text-red-800 mb-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Required: Explain why you're submitting late
              </label>
              <textarea
                value={lateReason}
                onChange={(e) => setLateReason(e.target.value)}
                className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                placeholder="Example: I needed more time to think through the options..."
                rows={2}
                maxLength={200}
              />
              <div className="flex items-center justify-between mt-1">
                <span className={`text-xs ${
                  containsProfanity(lateReason)
                    ? 'text-red-700'
                    : lateReason.trim().length < 10 
                    ? 'text-red-700' 
                    : 'text-green-700'
                }`}>
                  {containsProfanity(lateReason)
                    ? '⚠️ Contains inappropriate language'
                    : `${lateReason.length}/200 characters ${lateReason.trim().length < 10 ? '(min 10)' : ''}`}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-stone-500">
              {userAlternatives.length}/{maxPerUser} alternatives submitted {userAlternatives.length === 0 && '(at least 1 required)'}
            </span>
            <button
              onClick={handleSubmitAlternative}
              disabled={!alternative.trim() || containsProfanity(alternative) || detectUnconstructiveContent(alternative).isUnconstructive || (isTimeExpired() && userAlternatives.length === 0 && (lateReason.trim().length < 10 || containsProfanity(lateReason)))}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Add Alternative
            </button>
          </div>
        </div>
      )}

      {/* "I'm Done" button — shows when user has at least 1 alternative and less than max */}
      {communicationDone && isMentee && canEdit && userAlternatives.length >= 1 && userAlternatives.length < maxPerUser && 
       !conflict.steps?.exploreAlternatives?.doneUsers?.includes(user.id) && (
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-700" />
            <h5 className="font-medium text-green-800">Finished Proposing?</h5>
          </div>
          <p className="text-sm text-green-700 mb-3">
            You've submitted {userAlternatives.length} alternative{userAlternatives.length !== 1 ? 's' : ''}. 
            You can add up to {maxPerUser - userAlternatives.length} more, or indicate you're done.
          </p>
          <button
            onClick={async () => {
              try {
                const doneUsers = conflict.steps?.exploreAlternatives?.doneUsers || [];
                const updatedDoneUsers = [...doneUsers, user.id];
                const menteeIds = conflict.mentees?.map(m => m.id) || [];
                const allDone = menteeIds.every(id => updatedDoneUsers.includes(id));

                const updated = {
                  ...conflict,
                  steps: {
                    ...conflict.steps,
                    exploreAlternatives: {
                      ...conflict.steps.exploreAlternatives,
                      doneUsers: updatedDoneUsers
                    }
                  },
                  updatedAt: new Date().toISOString(),
                  updatedBy: user.id
                };

                // If all mentees are done, move to evaluate-select
                if (allDone) {
                  // Feature 2: Check for resolution mentoring gate
                  let mentorGateActive = false;
                  if (mentorIdsForConflict.length > 0) {
                    try {
                      const msId = mentorIdsForConflict[0];
                      const ms = await storage.get(`mentor-settings:${msId}`, true);
                      if (ms) {
                        const ov = ms.conflictOverrides?.[conflict.id]?.resolutionMentoringEnabled;
                        mentorGateActive = ov !== undefined ? ov : ms.resolutionMentoringEnabled;
                      }
                    } catch(e) {}
                  }
                  if (mentorGateActive) {
                    updated.steps.exploreAlternatives.mentorReviewRequired = true;
                    updated.steps.exploreAlternatives.mentorReviewComplete = false;
                    mentorIdsForConflict.forEach(mid => {
                      sendPushToUser(mid, 'Alternatives Ready for Review', `${conflict.title}: Solutions ready for your review.`, { conflictId: conflict.id });
                    });
                  } else {
                    updated.status = 'evaluate-select';
                    updated.statusHistory = [
                      ...conflict.statusHistory,
                      { status: 'evaluate-select', timestamp: new Date().toISOString() }
                    ];
                  }
                }

                addAuditEntry(updated, user.id, user.name, 'Marked done proposing alternatives', null, user.email);
                await storage.set(`conflict:${conflict.id}`, updated, true);
                await onUpdate();

                if (allDone) {
                  toast('All participants are done proposing alternatives. Moving to Evaluate & Select!');
                } else {
                  toast('You\'ve indicated you\'re done. Waiting for the other participant to finish.');
                }
                if (onReturnToOverview) onReturnToOverview();
              } catch (error) {
                console.error('Error marking done:', error);
                toast('Failed to save. Please try again.');
              }
            }}
            className="w-full bg-green-700 hover:bg-green-800 text-white py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            I'm Done Proposing Alternatives
          </button>
        </div>
      )}

      {/* Show "done" status */}
      {conflict.steps?.exploreAlternatives?.doneUsers?.includes(user.id) && conflict.status === 'explore-alternatives' && !conflict.steps?.exploreAlternatives?.mentorReviewRequired && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-blue-800 font-medium flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            You're done proposing alternatives
          </p>
          <p className="text-blue-600 text-sm mt-1">
            Waiting for the other participant to finish proposing their alternatives...
          </p>
        </div>
      )}

      {/* Feature 2: Mentor Review Required — Waiting message for mentees */}
      {conflict.steps?.exploreAlternatives?.mentorReviewRequired && !conflict.steps?.exploreAlternatives?.mentorReviewComplete && !isMentor && (
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <p className="text-purple-800 font-medium flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Waiting for Mentor Review
          </p>
          <p className="text-purple-600 text-sm mt-1">
            All alternatives have been submitted. The mentor is reviewing them before voting can begin.
          </p>
        </div>
      )}

      {/* Feature 2: Mentor Review Panel — for the mentor to review and acknowledge each alternative */}
      {conflict.steps?.exploreAlternatives?.mentorReviewRequired && !conflict.steps?.exploreAlternatives?.mentorReviewComplete && isMentor && (
        <div className="p-5 bg-purple-50 rounded-lg border-2 border-purple-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h5 className="font-medium text-purple-800">Review Proposed Solutions</h5>
              <p className="text-sm text-purple-600">Acknowledge each alternative before participants can vote. You may edit text if needed.</p>
            </div>
          </div>
          <div className="space-y-3">
            {alternatives.map((alt) => {
              const acked = alt.mentorAcknowledged;
              return (
                <div key={alt.id} className={`bg-white rounded-lg p-4 border-2 ${acked ? 'border-green-300' : 'border-purple-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      {alt.mentorEditedBy && (
                        <div className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded inline-block mb-1">✏️ Edited by mentor {alt.originalText ? '(original available)' : ''}</div>
                      )}
                      <p className="text-stone-700">{alt.text}</p>
                      <div className="text-xs text-stone-500 mt-1">By {alt.createdByName || alt.createdByEmail}</div>
                    </div>
                    {acked ? (
                      <div className="flex items-center gap-1 text-green-700 text-xs font-medium flex-shrink-0"><CheckCircle className="w-4 h-4" /> Acknowledged</div>
                    ) : (
                      <div className="flex gap-2 flex-shrink-0">
                        <button onClick={async () => {
                          const updatedAlts = alternatives.map(a => a.id === alt.id ? { ...a, mentorAcknowledged: true, mentorAcknowledgedAt: new Date().toISOString() } : a);
                          const allAcked = updatedAlts.every(a => a.mentorAcknowledged);
                          const up = { ...conflict, steps: { ...conflict.steps, exploreAlternatives: { ...conflict.steps.exploreAlternatives, alternatives: updatedAlts, mentorReviewComplete: allAcked } }, updatedAt: new Date().toISOString(), updatedBy: user.id };
                          if (allAcked) {
                            up.status = 'evaluate-select';
                            up.statusHistory = [...(up.statusHistory || []), { status: 'evaluate-select', timestamp: new Date().toISOString() }];
                            // Notify participants that voting is now unlocked
                            (conflict.mentees || []).forEach(m => sendPushToUser(m.id, 'Voting Unlocked', `${conflict.title}: The mentor has reviewed all alternatives. You can now rate solutions!`, { conflictId: conflict.id }));
                          }
                          addAuditEntry(up, user.id, user.name, 'Acknowledged alternative', alt.text.substring(0, 60), user.email);
                          await storage.set(`conflict:${conflict.id}`, up, true);
                          await onUpdate();
                          toast(allAcked ? 'All alternatives reviewed! Voting is now open. 🎉' : 'Alternative acknowledged');
                        }} className="px-3 py-1.5 bg-green-700 hover:bg-green-800 text-white text-xs rounded-lg font-medium">Acknowledge</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-purple-600">{alternatives.filter(a => a.mentorAcknowledged).length}/{alternatives.length} acknowledged</div>
        </div>
      )}

      {alternatives.length === 0 && (
        <div className="text-center py-12 text-stone-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No alternatives proposed yet</p>
        </div>
      )}
    </div>
  );
}

// Conflict History Component

export default ExploreAlternativesStep;
